import type { Dispatch, RefObject, SetStateAction } from 'react';
import { Checkbox, ConfigProvider, Flex, Typography } from 'antd';
import type { GlobalToken } from 'antd/es/theme/interface';
import type {
  CampaignsByCategory,
  PageType,
  TableColumn,
  TableData,
  VisibleCategories,
} from '@/types';
import campaignTypes from '@/data/campaignTypes.json';
import {
  CampaignCategory,
  formatCurrency,
  formatNumber,
  formatPercentage,
  replaceUnderscoreWithSpace,
  uppercaseFirstLetter,
} from '@/utils';
import { ImpressionsNotApplicableTooltipEcho } from '@/components/tooltips/ImpressionsTooltipsEcho';

/**
 * This is the table data for the campaign types table.
 * @param apiData - The API data.
 * @param pageType - The type of page. 'earnings' or 'payouts'
 * @param visibleCategories - The visible campaign categories.
 * @param setVisibleCategories - The function to set the visible categories.
 * @param popoverRefs - The popover refs.
 * @param token - The Ant Design theme token.
 * @returns The table data.
 */
const TableContentEcho = (
  apiData: CampaignsByCategory | undefined,
  pageType: PageType,
  visibleCategories: VisibleCategories,
  setVisibleCategories: Dispatch<SetStateAction<VisibleCategories>>,
  popoverRefs: {
    brandedContent: RefObject<HTMLDivElement>;
    social: RefObject<HTMLDivElement>;
    other: RefObject<HTMLDivElement>;
  },
  token: GlobalToken
): TableData[] => {
  if (!apiData) return [];
  const { ads, programmatic, sponsorships } = apiData;

  const handleCheckboxChange = (campaignCategory: CampaignCategory) => {
    setVisibleCategories({
      ...visibleCategories,
      [campaignCategory]: {
        ...visibleCategories[campaignCategory],
        visible: !visibleCategories[campaignCategory].visible,
      },
    });
  };

  const checkboxColumn = (campaignCategory: CampaignCategory) => {
    const checkboxColor = visibleCategories[campaignCategory].color;
    return (
      <ConfigProvider
        theme={{ token: { colorPrimary: checkboxColor, colorPrimaryHover: checkboxColor } }}
      >
        <Checkbox
          name={campaignCategory}
          checked={visibleCategories[campaignCategory].visible}
          onChange={() => handleCheckboxChange(campaignCategory)}
        />
      </ConfigProvider>
    );
  };

  const campaignTypeColumn = (campaignType: CampaignCategory) => {
    return (
      <Flex vertical gap={token.marginXXS}>
        <Typography.Text strong>
          {uppercaseFirstLetter(replaceUnderscoreWithSpace(campaignType))}
        </Typography.Text>
        {campaignTypes[campaignType].length > 0 && (
          <Typography.Text style={{ color: token.colorTextSecondary, fontSize: token.fontSizeSM }}>
            {campaignTypes[campaignType].join(', ')}
          </Typography.Text>
        )}
      </Flex>
    );
  };

  const impressionsNotApplicableColumn = (
    campaignCategory: 'brandedContent' | 'social' | 'other'
  ) => {
    return <ImpressionsNotApplicableTooltipEcho popoverRef={popoverRefs[campaignCategory]} />;
  };

  const earningsColumn = (campaignCategory: CampaignCategory) => {
    return (
      <Typography.Text strong>
        {formatCurrency(
          apiData[campaignCategory]?.total?.amount ?? 0,
          apiData[campaignCategory]?.total?.currency
        )}
      </Typography.Text>
    );
  };

  const percentageColumn = (campaignCategory: CampaignCategory) => {
    return (
      <Typography.Text
        style={{ color: token.colorTextSecondary, paddingInlineEnd: token.paddingLG }}
        data-testid={`percentage-${campaignCategory}`}
      >
        {formatPercentage(apiData[campaignCategory].percentage, { round: false, decimals: 2 })}%
      </Typography.Text>
    );
  };

  const campaignTypesCategoryConfig = [
    {
      id: 'ads',
      category: CampaignCategory.ADS,
      data: ads,
      hasImpressions: true,
    },
    {
      id: 'programmatic',
      category: CampaignCategory.PROGRAMMATIC,
      data: programmatic,
      hasImpressions: true,
    },
    {
      id: 'sponsorships',
      category: CampaignCategory.SPONSORSHIPS,
      data: sponsorships,
      hasImpressions: true,
    },
    {
      id: 'brandedContent',
      category: CampaignCategory.BRANDED_CONTENT,
      data: apiData.branded_content,
      hasImpressions: false,
      impressionsRef: 'brandedContent',
    },
    {
      id: 'social',
      category: CampaignCategory.SOCIAL,
      data: apiData.social,
      hasImpressions: false,
      impressionsRef: 'social',
    },
    {
      id: 'other',
      category: CampaignCategory.OTHER,
      data: apiData.other,
      hasImpressions: false,
      impressionsRef: 'other',
    },
  ];

  const generateRowColumns = (
    config: (typeof campaignTypesCategoryConfig)[number]
  ): TableColumn[] => {
    const columns: TableColumn[] = [];
    const { category, data, hasImpressions, impressionsRef } = config;

    if (pageType !== 'payouts') {
      columns.push({
        id: 'checkbox',
        value: checkboxColumn(category),
        style: { paddingInlineStart: token.paddingLG, paddingInlineEnd: token.paddingSM, width: 54 },
      });
    }

    columns.push({
      id: 'campaignType',
      value: campaignTypeColumn(category),
      style: pageType === 'payouts'
        ? { height: 77, paddingInlineStart: token.paddingLG }
        : { height: 77 },
    });

    if (hasImpressions) {
      columns.push({
        id: 'impressions',
        type: 'number',
        value: formatNumber('en', data.impressions),
        style: { textAlign: 'right' },
      });
    } else {
      columns.push({
        id: 'impressions',
        type: 'number',
        value: (
          <Flex justify="flex-end">
            {impressionsNotApplicableColumn(
              impressionsRef as 'brandedContent' | 'social' | 'other'
            )}
          </Flex>
        ),
        style: { textAlign: 'right' },
      });
    }

    columns.push({
      id: 'earnings',
      type: 'number',
      value: earningsColumn(category),
      style: { textAlign: 'right' },
    });

    columns.push({
      id: 'percentage',
      type: 'percentage',
      value: percentageColumn(category),
      style: { textAlign: 'right' },
    });

    return columns;
  };

  return campaignTypesCategoryConfig.map((config) => ({
    id: config.id,
    columns: generateRowColumns(config),
  }));
};

export default TableContentEcho;
